import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Avatar, Button, Dropdown, Space, Spin, Typography, message } from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CopyOutlined,
  DownOutlined,
  GlobalOutlined,
  PrinterOutlined,
  ShareAltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as newsService from '../../../services/newsService';
import { getFileUrl } from '../../../utils/urlUtils';
import { normalizeLanguageCode } from '../../../utils/languagePreference';
import { useAuth } from '../../../context/AuthContext';
import LIFELogo from '../../Home/LIFE_logo.png';
import styles from './NewsDetail.module.css';

const { Title, Text } = Typography;

const getActiveLanguageCode = (i18n) => normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en';
const DATE_DISPLAY_FORMAT = 'DD MMM YYYY';
const inferLanguageCode = (instance) => {
  const value = `${instance?.countryLanguage || ''} ${instance?.countryName || ''}`.trim().toLowerCase();

  if (!value) return null;
  if (value.includes('english') || value.includes('englisch') || value.includes('anglais') || value.includes('engels')) return 'en';
  if (value.includes('deutsch') || value.includes('german') || value.includes('allemand') || value.includes('duits')) return 'de';
  if (value.includes('français') || value.includes('francais') || value.includes('french') || value.includes('frans')) return 'fr';
  if (value.includes('nederlands') || value.includes('dutch') || value.includes('néerlandais') || value.includes('niederländisch')) return 'nl';

  return null;
};

/**
 * Public News detail page with language-aware content, anonymous visit tracking, sharing, print, and QR tools.
 */
const NewsDetail = () => {
  const { id, slug } = useParams();
  const { t, i18n } = useTranslation();
  const { authInitialized, isAuthenticated } = useAuth();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestedLanguageCode, setRequestedLanguageCode] = useState(getActiveLanguageCode(i18n));
  const recordedKeyRef = useRef(null);

  const languageCode = getActiveLanguageCode(i18n);
  const articleLanguageCode = requestedLanguageCode || languageCode;
  const newsId = parseInt(id, 10);
  const publishedDate = news?.publishedAt ? dayjs(news.publishedAt) : dayjs(news?.dateModified);
  const modifiedDate = dayjs(news?.dateModified);
  const publishedDateText = publishedDate.isValid() ? publishedDate.format(DATE_DISPLAY_FORMAT) : '';
  const modifiedDateText = modifiedDate.isValid() ? modifiedDate.format(DATE_DISPLAY_FORMAT) : '';
  const showLastUpdated = !!(
    news?.publishedAt &&
    publishedDate.isValid() &&
    modifiedDate.isValid() &&
    modifiedDate.diff(publishedDate, 'minute') >= 1
  );
  const publicUrl = useMemo(() => news ? `${window.location.origin}${newsService.getNewsPublicPath(news)}` : window.location.href, [news]);
  const languageItems = useMemo(() => {
    const seenCodes = new Set();

    return (news?.availableInstances || [])
      .map((instance) => {
        const code = inferLanguageCode(instance);
        if (!code || seenCodes.has(code)) {
          return null;
        }

        seenCodes.add(code);

        return {
          key: code,
          label: instance.countryLanguage || instance.countryName || code.toUpperCase(),
        };
      })
      .filter(Boolean);
  }, [news]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const result = await newsService.getPublicNewsDetail(newsId, slug, articleLanguageCode, { suppressNotification: true });
      setNews(result);
    } catch (error) {
      setNews(null);
    } finally {
      setLoading(false);
    }
  }, [articleLanguageCode, newsId, slug]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setRequestedLanguageCode(languageCode);
  }, [languageCode, newsId]);

  useEffect(() => {
    if (!news || !authInitialized || isAuthenticated) return;
    const key = `${news.id}:${news.instanceId}:${articleLanguageCode}`;
    if (recordedKeyRef.current === key) return;
    recordedKeyRef.current = key;
    newsService.recordNewsVisit(news.id, {
      languageCode: articleLanguageCode,
      newsInstanceId: news.instanceId,
      referrer: document.referrer || null,
    }, { suppressNotification: true }).catch(() => {});
  }, [articleLanguageCode, authInitialized, isAuthenticated, news]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    message.success(t('news.linkCopied', 'News link copied'));
  };

  const handleShare = async () => {
    const shareData = {
      title: news.title,
      text: news.excerpt,
      url: publicUrl,
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        // Fall back to copying the link when native share is cancelled or unavailable.
      }
    }

    handleCopyLink();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleLanguageSelect = ({ key }) => {
    if (!key || key === articleLanguageCode) {
      return;
    }

    setRequestedLanguageCode(key);
  };

  if (loading) {
    return <PagesWrapper><div className={styles.loading}><Spin size="large" /></div></PagesWrapper>;
  }

  if (!news) {
    return (
      <PagesWrapper>
        <div className={styles.container}>
          <Alert type="warning" showIcon message={t('news.notFound', 'News was not found or is not published.')} />
          <Link to="/news"><Button icon={<ArrowLeftOutlined />} className={styles.backButton}>{t('news.backToNews', 'Back to News')}</Button></Link>
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <>
        <article className={styles.articleShell}>
          {news.coverImageUrl && (
            <div className={styles.coverHero}>
              <img src={getFileUrl(news.coverImageUrl)} alt={news.title} />
            </div>
          )}

          <div className={styles.container}>
            <div className={styles.backRow}>
              <Link to="/news" className={styles.backLink}><ArrowLeftOutlined /> {t('news.backToNews', 'Back to News')}</Link>
              {languageItems.length > 1 && (
                <Dropdown
                  menu={{
                    items: languageItems,
                    selectable: true,
                    selectedKeys: [articleLanguageCode],
                    onClick: handleLanguageSelect,
                  }}
                  trigger={[ 'click' ]}
                  placement="bottomRight"
                >
                  <button type="button" className={styles.languageDropdown}>
                    <GlobalOutlined />
                    <span>{news.countryLanguage || t('news.availableLanguages', 'Available languages')}</span>
                    <DownOutlined />
                  </button>
                </Dropdown>
              )}
            </div>

            {news.isFallback && (
              <Alert
                type="info"
                showIcon
                className={styles.fallbackAlert}
                message={t('news.fallbackNotice', 'This article is not available in your selected language. Showing another available language.')}
              />
            )}

            <header className={styles.headerBlock}>
              <div className={styles.titleRow}>
                <Title className={styles.title}>{news.title}</Title>
                <div className={styles.titleTools}>
                  <span className={styles.readTime}>{t('news.readingTime', '{{count}} min read', { count: news.readingTimeMinutes || 1 })}</span>
                  <div className={styles.iconActions}>
                    <Button type="text" icon={<ShareAltOutlined />} aria-label={t('news.share', 'Share')} onClick={handleShare} className={styles.iconButton} />
                    <Button type="text" icon={<CopyOutlined />} aria-label={t('news.copyLink', 'Copy link')} onClick={handleCopyLink} className={styles.iconButton} />
                    <Button type="text" icon={<PrinterOutlined />} aria-label={t('news.print', 'Print')} onClick={handlePrint} className={styles.iconButton} />
                  </div>
                </div>
              </div>
              <div className={styles.metaRow}>
                <Space className={styles.author}>
                  <Avatar src={getFileUrl(news.addedByProfileImageUrl)} icon={<UserOutlined />} />
                  <Text>{news.addedByName || t('news.editorialTeam', 'HeatCraftHP editorial team')}</Text>
                </Space>
                <Space wrap className={styles.meta}>
                  {publishedDateText && <span><CalendarOutlined /> {publishedDateText}</span>}
                  {showLastUpdated && <span>{t('news.lastUpdated', 'Last updated')}: {modifiedDateText}</span>}
                </Space>
              </div>
            </header>

            <div className={styles.titleDivider} />

            <div className={styles.contentShell}>
              <div className={styles.richContent} dangerouslySetInnerHTML={{ __html: news.body }} />
            </div>
          </div>
        </article>

        <section className={styles.disclaimerSection}>
          <div className={styles.disclaimerInner}>
            <div className={styles.disclaimerBlock}>
              <img
                src={LIFELogo}
                alt="Co-funded by the European Union"
                className={styles.disclaimerImage}
              />
              <Text className={styles.disclaimerText}>
                Co-funded by the European Union. Views and opinions expressed are however those of the
                author(s) only and do not necessarily reflect those of the European Union or CINEA.
                Neither the European Union nor the granting authority can be held responsible for them.
              </Text>
            </div>
          </div>
        </section>
      </>
    </PagesWrapper>
  );
};

export default NewsDetail;
